import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowRight, BookOpen, Zap, BarChart3, Globe, GraduationCap } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '@/types/language';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Index = () => {
  useSeoMeta({
    title: 'LinguaNostr - Language Learning Reimagined',
    description: 'Master languages with evidence-based techniques, spaced repetition, and social learning.',
  });

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 p-8 text-white shadow-xl sm:p-12">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L3N2Zz4=')] opacity-40"></div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 text-2xl font-bold backdrop-blur-sm">
              L
            </div>
            <span className="text-sm font-medium uppercase tracking-wider text-blue-100">Vibed with <a href="https://shakespeare.diy" className="underline hover:text-white">Shakespeare</a></span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Learn Languages. Better.
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-blue-100">
            Evidence-based learning with Gianfranco Conti's EPI methodology, 
            corpus frequency lists, spaced repetition, and Nostr-powered social learning.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/lessons">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50">
                Start Learning
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/practice">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                Review Flashcards
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { icon: Globe, label: 'Languages', value: '8' },
          { icon: BookOpen, label: 'Lessons', value: '24+' },
          { icon: Zap, label: 'Words', value: '1000+' },
          { icon: BarChart3, label: 'CEFR Levels', value: 'A1-C2' },
        ].map((stat) => (
          <Card key={stat.label} className="border-slate-200 bg-white/50 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/50">
            <CardHeader className="flex flex-row items-center gap-3 pb-2">
              <stat.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {stat.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Language Selection */}
      <section>
        <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
          Choose Your Language
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {SUPPORTED_LANGUAGES.filter((l) => l.code !== 'en').map((lang) => (
            <Link key={lang.code} to={`/lessons?lang=${lang.code}`}>
              <Card className="group cursor-pointer border-slate-200 transition-all hover:border-blue-300 hover:shadow-md dark:border-slate-800 dark:hover:border-blue-700">
                <CardContent className="flex items-center gap-4 p-5">
                  <span className="text-4xl">{lang.flag}</span>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">{lang.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{lang.nativeName}</p>
                    <div className="mt-1 flex gap-1">
                      {lang.levelsAvailable.map((level) => (
                        <span key={level} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                          {level}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ArrowRight className="ml-auto h-5 w-5 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Methodology Section */}
      <section className="rounded-2xl bg-gradient-to-br from-slate-50 to-white p-8 dark:from-slate-900 dark:to-slate-950">
        <h2 className="mb-6 text-2xl font-bold text-slate-900 dark:text-white">
          Our Pedagogical Approach
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Conti's EPI Method",
              desc: 'MARSEARS framework with Sentence Builders, narrow reading/listening, and structured production. Research-backed second language acquisition.',
              icon: GraduationCap,
            },
            {
              title: 'Frequency-Based Vocabulary',
              desc: 'Learn the most common words first. Corpus-derived word lists focused on high-frequency, high-utility vocabulary organized by CEFR level.',
              icon: Zap,
            },
            {
              title: 'Spaced Repetition (SRS)',
              desc: 'SM-2 algorithm optimizes review intervals based on the forgetting curve. Maximizes retention with minimal study time.',
              icon: BarChart3,
            },
          ].map((item) => (
            <Card key={item.title} className="border-slate-200 bg-white/70 dark:border-slate-800 dark:bg-slate-900/70">
              <CardHeader>
                <item.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                <CardTitle className="text-lg text-slate-900 dark:text-white">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {item.desc}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Index;
